/* eslint-disable max-len */
import { DownOutlined } from '@ant-design/icons';
import { Col, Collapse, Divider, Form, Modal, Row, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';
import { RowTitles } from 'components/molecules/RowTitles';
import ErrModal from 'components/molecules/SpecialniSluzby/ErrModal';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import { ETypPrevodu } from 'services/specialniSluzby/uePozadavky';
import { ueZruseni } from 'services/specialniSluzby/ueZruseni';
import uePozadavkyDetailDVPApi, {
    IFilterDVP,
    IPozadavkyDVP,
    IPozadavkyPolozkaDVP,
    uePozadavkyZmazaniDVPApi,
    ueZaevidovaniDVP,
    ueZaevidovaniPostDVP,
    ueZmazaniDeleteDVP
} from 'services/specialniSluzby/uevDVP';

import routePathEnum from 'enums/routePathEnum';
import { EStavPozadavku } from 'enums/specialniSluzby/stavPozadavku';
import { ETypSeznam } from 'enums/specialniSluzby/typSeznam';

import { typStavZadostiSelect } from 'data/codebooks/specialniSluzby/typStavZadosti';

import formatInputNumberThousand from 'utils/formatInputNumberThousand';
import getLanguageObject from 'utils/getLanguageObject';
import dveDesetinneMistaValidation from 'utils/validations/dveDesetinneMistaValidation';
import numberValidation from 'utils/validations/numberValidation';

import NovyPozadavekDVP from './NovyPozadavekDVP';

const rowTitleStyles = {
    maxWidth: '650px',
    marginBottom: '6px',
    fontSize: '18px'
};

interface IPagination {
    offset: number;
    page: number;
    limit: number;
    order: string;
}

const paginationDefaultVals = {
    offset: 0,
    page: 1,
    limit: 10,
    order: 'poradi:desc:idpolozky:desc'
};

interface IDvpSeznam {
    setRefetchDFP: React.Dispatch<React.SetStateAction<boolean>>;
    typprevodu?: ETypPrevodu;
    setIsDvpDetail?: React.Dispatch<React.SetStateAction<boolean>>;
    isAfterMezniDatum?: boolean;
}

const DvpSeznam: React.FC<IDvpSeznam> = ({ typprevodu, setIsDvpDetail, setRefetchDFP, isAfterMezniDatum }) => {
    const { confirm } = Modal;
    const Text = Typography.Text;
    const { Panel } = Collapse;
    const { aktivniFirma, specialniSluzby, specialniSluzbyDVP, language } = useContext(Context);
    const { setLevel } = useProfily();
    const { navigateWorkaround } = useNavigateWorkaround();
    const [form] = Form.useForm();
    const [form2] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(false);

    const [data, setData] = useState<IPozadavkyDVP | undefined>();
    const [dataZmazani, setDataZmazani] = useState<IPozadavkyDVP | undefined>();
    const [dataLze, setDataLze] = useState<IPozadavkyDVP | undefined>();
    const [dataNelze, setDataNelze] = useState<IPozadavkyDVP | undefined>();

    const [pagination, setPagination] = useState<IPagination>(paginationDefaultVals);
    const [paginationZmazani, setPaginationZmazani] = useState<IPagination>(paginationDefaultVals);
    const [paginationNelze, setPaginationNelze] = useState<IPagination>(paginationDefaultVals);
    const [paginationLze, setPaginationLze] = useState<IPagination>(paginationDefaultVals);

    const [errModalMsg, setErrModalMsg] = useState<string>();
    const [modalMessage, setModalMessage] = useState<string>('');
    const [zaevidovaniErr, setZaevidovaniErr] = useState<string[]>([]);

    const [selectedDetail, setSelectedDetail] = useState<IPozadavkyPolozkaDVP>();
    const [zmenaPozadavku, setZmenaPozadavku] = useState<'edit' | 'novy' | 'zaevidovat' | 'smazat' | undefined>();

    const isUEN = !!data?.pocetcpvn;

    const loadData = () => {
        if (zmenaPozadavku === 'zaevidovat') {
            setLoading(true);
            const promisses = [];
            promisses.push(
                new Promise(resolve => {
                    const resProm = ueZaevidovaniDVP(specialniSluzby?.detail?.id!, {
                        ...specialniSluzbyDVP?.detailFiltrDVP!,
                        evidovat: true,
                        offset: paginationNelze.offset,
                        limit: paginationNelze.limit
                    });
                    if (resProm)
                        resProm
                            .then((resp: IPozadavkyDVP) => {
                                setDataLze(resp);
                            })
                            .finally(() => {
                                resolve(true);
                            });
                })
            );

            promisses.push(
                new Promise(resolve => {
                    const resProm = ueZaevidovaniDVP(specialniSluzby?.detail?.id!, {
                        ...specialniSluzbyDVP?.detailFiltrDVP!,
                        evidovat: false,
                        offset: paginationLze.offset,
                        limit: paginationLze.limit
                    });
                    if (resProm)
                        resProm
                            .then((resp: IPozadavkyDVP) => {
                                setDataNelze(resp);
                            })
                            .finally(() => {
                                resolve(true);
                            });
                })
            );

            Promise.all(promisses).finally(() => {
                setLoading(false);
            });
        } else if (zmenaPozadavku === 'smazat') {
            setLoading(true);
            uePozadavkyZmazaniDVPApi(specialniSluzby?.detail?.id!, {
                ...specialniSluzbyDVP?.detailFiltrDVP!,
                offset: paginationZmazani.offset,
                limit: paginationZmazani.limit
            })
                .then((resp: IPozadavkyDVP) => {
                    setDataZmazani(resp);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
        if (!zmenaPozadavku) {
            setLoading(true);
            const formItems = Object.fromEntries(
                Object.entries({
                    ...form.getFieldsValue()
                }).filter(([, v]) => v !== undefined && v !== '')
            ) as unknown as IFilterDVP;
            formItems.offset = pagination.offset !== undefined ? pagination.offset : 0;
            formItems.limit = pagination.limit;
            formItems.order = pagination.order;

            // načtu všechny požadavky (případně dle nastaveného filtru)
            uePozadavkyDetailDVPApi(specialniSluzby?.detail?.id!, formItems)
                .then((resp: IPozadavkyDVP) => {
                    setLevel('specialniSluzbyDVP', {
                        ...specialniSluzbyDVP,
                        detailDVP: {
                            ...specialniSluzbyDVP?.detailDVP,
                            ...resp,
                            mena: resp.mena ?? specialniSluzbyDVP?.detailDVP?.mena
                        } as IPozadavkyDVP,
                        detailFiltrDVP: formItems
                    });
                    setData({
                        ...resp,
                        mena: resp.mena ?? specialniSluzbyDVP?.detailDVP?.mena
                    });
                    form2.resetFields();
                })
                .catch((err: any) => {
                    err.response?.status === 404 && setData(undefined);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const columnsDVP = [
        {
            title: <TranslateText>KOD_UCASTNIKA</TranslateText>,
            fixed: true,
            dataIndex: 'koducastnika',
            key: 'koducastnika',
            sorter: true
        },
        {
            title: <TranslateText>DVP_IDENTIFIKACE_KLIENTA</TranslateText>,
            dataIndex: 'kodklienta',
            key: 'kodklienta',
            sorter: true
        },
        {
            title: <TranslateText>POCET_UPIS_CP</TranslateText>,
            dataIndex: 'pocetcp',
            key: 'pocetcp',
            render: (val: string) => formatInputNumberThousand(val || 0),
            sorter: true
        },
        {
            title: <TranslateText>DVP_CELKOVA_CASTKA</TranslateText>,
            dataIndex: 'castka',
            key: 'castka',
            render: (val: string) =>
                `${formatInputNumberThousand(val || 0)} ${specialniSluzbyDVP?.detailDVP?.mena || ''}`,
            sorter: true
        },
        /* {
            title: <TranslateText>ID_PREVODU_V_CDCP</TranslateText>,
            dataIndex: 'idvcdcp',
            key: 'idvcdcp'
            sorter: true,
        }, */
        {
            title: <TranslateText>STAV</TranslateText>,
            dataIndex: 'stav',
            key: 'stav',
            sorter: true,
            render: (text: string, row: IPozadavkyPolozkaDVP) =>
                getLanguageObject(language)['SS_DETAIL_STAV_' + text] +
                (row.sparovano ? '(' + getLanguageObject(language).SPAROVANO + ')' : '')
        },
        ...(zmenaPozadavku
            ? []
            : [
                  {
                      title: <TranslateText>PODROBNOSTI</TranslateText>,
                      dataIndex: 'podrobnosti',
                      key: 'podrobnosti',
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      fixed: 'right' as any,
                      render: (_text: string, row: IPozadavkyPolozkaDVP, index: number) => (
                          <AnchorComponent
                              id={`zobrazitBtn${index}`}
                              onClick={() => {
                                  setSelectedDetail(row);
                                  setLevel('specialniSluzbyDVP', {
                                      ...specialniSluzbyDVP,
                                      pozadavek: {
                                          ...row
                                      }
                                  });
                              }}
                          >
                              <TranslateText>ZOBRAZIT_DETAIL</TranslateText>
                          </AnchorComponent>
                      )
                  }
              ])
    ];

    const rowsSpan: number = 12;
    const columnsDetail = (
        <>
            {specialniSluzby?.detail?.isin && (
                <RowTitles
                    firstText="ISIN"
                    secondText={String(specialniSluzby?.detail.isin)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail.lzeevidovatod && (
                <RowTitles
                    firstText="DATUM"
                    secondText={String(specialniSluzby ? dayjs(selectedDetail.lzeevidovatod).format('DD.MM.YYYY') : '')}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail.koducastnika && (
                <RowTitles
                    firstText="KOD_UCASTNIKA"
                    secondText={String(selectedDetail && selectedDetail.koducastnika)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail.kodklienta && (
                <RowTitles
                    firstText="DVP_IDENTIFIKACE_KLIENTA"
                    secondText={String(selectedDetail && selectedDetail.kodklienta)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
            {selectedDetail && selectedDetail.pocetcp && (
                <RowTitles
                    firstText="POCET_UPIS_CP"
                    secondText={formatInputNumberThousand(String(selectedDetail && selectedDetail.pocetcp))}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}

            {specialniSluzbyDVP?.detailDVP?.castkacelkem && (
                <RowTitles
                    firstText="DVP_CELKOVA_CASTKA"
                    secondText={formatInputNumberThousand(String(specialniSluzbyDVP?.detailDVP.castkacelkem))}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}

            {specialniSluzbyDVP?.detailDVP?.mena && (
                <RowTitles
                    firstText="MENA"
                    secondText={String(specialniSluzbyDVP?.detailDVP?.mena)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}

            {selectedDetail && selectedDetail.stav && (
                <RowTitles
                    firstText="STAV"
                    secondText={String(
                        getLanguageObject(language)['SS_DETAIL_STAV_' + selectedDetail.stav] +
                            (selectedDetail.sparovano ? '(' + getLanguageObject(language).SPAROVANO + ')' : '')
                    )}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={rowsSpan}
                />
            )}
        </>
    );

    useEffect(() => {
        setIsDvpDetail?.(!!zmenaPozadavku);
        if (
            (zmenaPozadavku === 'smazat' || zmenaPozadavku === 'zaevidovat' || !zmenaPozadavku) &&
            specialniSluzbyDVP?.detailDVP
        ) {
            loadData();
            setRefetchDFP(pv => !pv);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zmenaPozadavku]);

    useEffect(() => {
        if (specialniSluzby?.detail?.id) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [specialniSluzby?.detail?.id, pagination]);

    useEffect(() => {
        if (zaevidovaniErr.length) {
            data &&
                setZaevidovaniErr([
                    ...(data.castka !== data.celkcastka ? ['castka'] : []),
                    // ...(data.celkpoccp !== data.pocetcp ? ['pocetcp'] : []),
                    ...(data.celkpoccp !== specialniSluzby?.detail?.pocetcp ? ['pocetcp'] : []),
                    ...(!data.bankucet && !data?.iban ? ['bankucet'] : [])
                ]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const renderModal = (msg: string) => {
        setModalMessage(msg);
    };

    const renderButtons = () => {
        return (
            <Row
                className="button-row"
                style={{
                    marginBottom: '22px'
                }}
            >
                {typprevodu !== ETypPrevodu.PROTIABEZZAPL && typprevodu !== ETypPrevodu.PXVNABEZZAPL && (
                    <ButtonComponent
                        id="zpetBtn"
                        buttontext="ZPET"
                        type="default"
                        onClick={() => {
                            navigateWorkaround(-1);
                        }}
                    />
                )}
                {!isAfterMezniDatum &&
                    specialniSluzby?.detail?.stav === EStavPozadavku.OTEVRENO &&
                    !specialniSluzby?.detail.puvodcdcp && (
                        <ButtonComponent
                            id="pridatPozadavekBtn"
                            buttontext="SS_PRIDAT_POZADAVEK"
                            onClick={() => {
                                setZmenaPozadavku('novy');
                            }}
                        />
                    )}
                {data &&
                    !isAfterMezniDatum &&
                    specialniSluzby?.detail?.stav === EStavPozadavku.OTEVRENO &&
                    !specialniSluzby?.detail.puvodcdcp && (
                        <ButtonComponent
                            id="zaevidovatBtn"
                            buttontext="SS_ZAEVIDOVAT_V_CDCP"
                            onClick={() => {
                                const zaevErrArr = [
                                    ...(data.castka !== data.celkcastka ? ['castka'] : []),
                                    // ...(data.celkpoccp !== data.pocetcp ? ['pocetcp'] : []),
                                    ...(data.celkpoccp !== specialniSluzby?.detail?.pocetcp ? ['pocetcp'] : []),
                                    ...(!data.bankucet && !data?.iban ? ['bankucet'] : [])
                                ];
                                if (zaevErrArr.length) {
                                    setZaevidovaniErr(zaevErrArr);
                                } else {
                                    setLevel('specialniSluzbyDVP', {
                                        ...specialniSluzbyDVP,
                                        detailDVP: {
                                            ...specialniSluzbyDVP?.detailDVP,
                                            polozky: (specialniSluzbyDVP?.detailDVP?.polozky || []).filter(
                                                e =>
                                                    e.stav !== 'U006' &&
                                                    (e.stav === 'U008'
                                                        ? e.lzeevidovatod
                                                            ? dayjs(e.lzeevidovatod) > dayjs().startOf('day')
                                                            : false
                                                        : e.stav === 'U007')
                                            )
                                        } as IPozadavkyDVP,
                                        seznamTyp: ETypSeznam.ZAEVIDOVANY
                                    });
                                    setZmenaPozadavku('zaevidovat');
                                }
                            }}
                        />
                    )}
                {!!data?.count &&
                    !isAfterMezniDatum &&
                    specialniSluzby?.detail?.stav === EStavPozadavku.OTEVRENO &&
                    !specialniSluzby?.detail.puvodcdcp && (
                        <ButtonComponent
                            id="smazatBtn"
                            buttontext="SMAZAT"
                            onClick={() => {
                                setLevel('specialniSluzbyDVP', {
                                    ...specialniSluzbyDVP,
                                    seznamTyp: ETypSeznam.SMAZANY
                                });
                                setZmenaPozadavku('smazat');
                            }}
                        />
                    )}
                {!isAfterMezniDatum &&
                    typprevodu !== ETypPrevodu.PROTIABEZZAPL &&
                    typprevodu !== ETypPrevodu.PXVNABEZZAPL &&
                    !data?.count &&
                    !specialniSluzby?.detail?.puvodcdcp && (
                        <ButtonComponent
                            buttontext="SMAZAT_POZADAVEK"
                            id="smazatBtn"
                            onClick={() => {
                                confirm({
                                    title: <TranslateText>POTVRDENI</TranslateText>,
                                    content: `${getLanguageObject(language).DELETE_CONFIRM_TEXT} ${
                                        getLanguageObject(language)[
                                            'SS_NOVY_POZADAVEK_NA_' + specialniSluzby?.detail?.typ
                                        ]
                                    } - ${getLanguageObject(language).ISIN}: ${specialniSluzby?.detail?.isin}, ${
                                        getLanguageObject(language).DATUM
                                    }: ${dayjs(specialniSluzby?.detail?.datum).format('DD.MM.YYYY')}`,
                                    cancelText: <TranslateText>NE</TranslateText>,
                                    okText: <TranslateText>ANO</TranslateText>,
                                    onOk() {
                                        setLoading(true);
                                        ueZruseni(specialniSluzby!.detail?.id!)
                                            .then(resp => {
                                                if (resp.stav?.includes('TIMEOUT')) {
                                                    setErrModalMsg('SS_SLUZBA_ERROR_NESKORO');
                                                } else {
                                                    setLoading(false);
                                                    navigateWorkaround(
                                                        // eslint-disable-next-line max-len
                                                        `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_PREHLED_AKTIVNICH_POZADAVKU}`
                                                    );
                                                }
                                            })
                                            .finally(() => setLoading(false));
                                    }
                                });
                            }}
                        />
                    )}
            </Row>
        );
    };

    const renderSelectedDetail = () => {
        return (
            <Modal
                width={700}
                open={!!selectedDetail}
                onCancel={() => setSelectedDetail(undefined)}
                title={<TranslateText>DVP_TITLE_DETAIL</TranslateText>}
                footer={[
                    <Row className="button-row-modal">
                        {!isAfterMezniDatum &&
                            selectedDetail &&
                            !specialniSluzby?.detail?.puvodcdcp &&
                            ['U001', 'U002', 'U003', 'U006', 'U007', 'U008', 'U700', 'U701', 'U730'].includes(
                                selectedDetail.stav || ''
                            ) && (
                                <ButtonComponent
                                    id="smazatVybranyBtn"
                                    buttontext="SS_DETAIL_VYBRANY_SMAZAT"
                                    onClick={() => {
                                        confirm({
                                            title: <TranslateText>POTVRDENI</TranslateText>,
                                            content: <TranslateText>UPIS_DVP_DELETE_SELECTED_CONFIRM</TranslateText>,
                                            cancelText: <TranslateText>NE</TranslateText>,
                                            okText: <TranslateText>ANO</TranslateText>,
                                            onOk() {
                                                setLoading(true);
                                                ueZmazaniDeleteDVP(specialniSluzby?.detail?.id!, {
                                                    idpolozky: selectedDetail.idpolozky,
                                                    ...specialniSluzbyDVP?.detailFiltrDVP
                                                })
                                                    .then(resp => {
                                                        if (resp.stav?.includes('TIMEOUT')) {
                                                            setErrModalMsg('SS_SLUZBA_ERROR_NESKORO');
                                                        } else {
                                                            setLoading(false);
                                                            setSelectedDetail(undefined);
                                                            loadData();
                                                        }
                                                    })
                                                    .finally(() => setLoading(false));
                                            }
                                        });
                                    }}
                                />
                            )}
                        {!isAfterMezniDatum &&
                            selectedDetail &&
                            !specialniSluzby?.detail?.puvodcdcp &&
                            ['U001', 'U002', 'U003', 'U006', 'U007', 'U008', 'U730'].includes(
                                selectedDetail.stav || ''
                            ) && (
                                <ButtonComponent
                                    id="zmenitVybranyBtn"
                                    buttontext="SS_DETAIL_VYBRANY_ZMENIT"
                                    onClick={() => {
                                        setLevel('specialniSluzbyDVP', {
                                            ...specialniSluzbyDVP,
                                            pozadavek: {
                                                ...selectedDetail
                                            }
                                        });
                                        setZmenaPozadavku('edit');
                                    }}
                                />
                            )}
                    </Row>
                ]}
            >
                {columnsDetail}
            </Modal>
        );
    };

    const renderTable = (
        showFilterAndButtons: boolean,
        paginationGetter: IPagination,
        paginationSetter: React.Dispatch<React.SetStateAction<IPagination>>,
        Tabledata?: IPozadavkyDVP
    ) => (
        <>
            {showFilterAndButtons && (
                <FormWrapper onFinish={() => loadData()} outForm={form}>
                    <Row gutter={8}>
                        <Col span={3}>
                            <InputComponent
                                name="koducastnika"
                                label="KOD_UCASTNIKA"
                                maxLength={3}
                                notRequired={true}
                            />
                        </Col>
                        <Col span={7}>
                            <InputComponent name="kodklienta" label="DVP_IDENTIFIKACE_KLIENTA" notRequired={true} />
                        </Col>
                        <Col span={4}>
                            <InputNumberComponent
                                name="pocetcp"
                                label="POCET_UPIS_CP"
                                maxLength={12}
                                precision={0}
                                notRequired={true}
                            />
                        </Col>
                        <Col span={6}>
                            <InputNumberComponent
                                name="castka"
                                label="DVP_CELKOVA_CASTKA"
                                rules={dveDesetinneMistaValidation(true)}
                                maxLength={12}
                                precision={2}
                                notRequired={true}
                            />
                        </Col>
                        <Col span={4}>
                            <SelectComponent
                                isFormItem={true}
                                data={typStavZadostiSelect}
                                name="stav"
                                labelParagraph="STAV_ZADOSTI"
                                notRequired={true}
                                showTooltipValue={true}
                            />
                        </Col>
                    </Row>
                    <Row
                        className="button-row-modal"
                        gutter={12}
                        style={{
                            position: 'relative',
                            top: -12
                        }}
                    >
                        <ButtonComponent id="filtrSubmitBtn" buttontext="FILTROVAT" onClick={() => form.submit()} />
                        <ButtonComponent
                            id="filterClearBtn"
                            buttontext="VSECHNY"
                            onClick={() => {
                                form.resetFields();
                                form.setFieldsValue({
                                    pocetcp: undefined,
                                    castka: undefined
                                });
                                loadData();
                            }}
                        />
                    </Row>
                </FormWrapper>
            )}
            <TableComponent
                rowKey={(_, index) => index || 0}
                columns={columnsDVP}
                scroll={{
                    x: true
                }}
                dataSource={Tabledata?.polozky}
                locale={{
                    emptyText: <TranslateText>TABULKA_ZADNE_ZAZNAMY</TranslateText>
                }}
                pagination={{
                    total: Tabledata?.count || 0,
                    onChange: page => {
                        paginationSetter({
                            page: page,
                            offset: (page - 1) * paginationGetter.limit,
                            limit: paginationGetter.limit,
                            order: paginationGetter.order
                        });
                    },
                    showSizeChanger: false,
                    defaultPageSize: 10
                }}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(paginat, _b, sorter: any) =>
                    paginationSetter(pv => {
                        return {
                            ...pv,
                            ...(sorter?.order && {
                                order: `${sorter.columnKey}:${
                                    sorter?.order === 'descend' ? 'desc' : 'asc'
                                }:idpolozky:desc`
                            }),
                            limit: paginat.pageSize || 10
                        };
                    })
                }
            />
            <FormWrapper onFinish={() => {}} outForm={form2}>
                <Row gutter={8}>
                    {Tabledata?.pocetcp ? (
                        <Col>
                            <InputComponent
                                name="pocetcp"
                                label="POCET_CP"
                                notRequired={true}
                                defaultValue={formatInputNumberThousand(Tabledata.pocetcp || 0)}
                                disabled={true}
                                rules={numberValidation(true)}
                            />
                        </Col>
                    ) : null}

                    {Tabledata?.pocetuctu ? (
                        <Col>
                            <InputComponent
                                name="pocetuctu"
                                label="POCET_UCASTNIKU"
                                notRequired={true}
                                defaultValue={formatInputNumberThousand(Tabledata.pocetuctu || 0)}
                                disabled={true}
                            />
                        </Col>
                    ) : null}
                    {/* specialniSluzby?.detail?.pocetuctu &&
                    Tabledata?.celkpocupis &&
                    typprevodu === ETypPrevodu.PROTIABEZZAPL ? (
                        <Col>
                            <InputComponent
                                name="celkpocupis"
                                label="DOKUMENT_DODATEK_DVP_POCET_UPISOVATELU"
                                notRequired={true}
                                defaultValue={`${formatInputNumberThousand(specialniSluzby?.detail?.pocetuctu)} / ${formatInputNumberThousand(Tabledata?.celkpocupis || 0)}`}
                                disabled={true}
                            />
                        </Col>
                    ) : null */}
                    {Tabledata?.castka ? (
                        <Col span={10}>
                            <InputComponent
                                name="castka"
                                label="CELKOVA_CASTKA"
                                notRequired={true}
                                defaultValue={`${formatInputNumberThousand(Tabledata.castka || 0)} / ${formatInputNumberThousand(Tabledata?.celkcastka || 0)}`}
                                disabled={true}
                            />
                        </Col>
                    ) : null}
                </Row>
            </FormWrapper>
            {showFilterAndButtons && renderButtons()}
        </>
    );

    return (
        <>
            {zmenaPozadavku === 'zaevidovat' ? (
                <>
                    <Title level={4} text="SS_SEZNAM_ZAEVIDOVAT_NELZE" />
                    {renderTable(false, paginationNelze, setPaginationNelze, dataNelze)}
                    <Title
                        style={{
                            paddingTop: '20px'
                        }}
                        level={4}
                        text="SS_SEZNAM_ZAEVIDOVAT_LZE"
                    />
                    {renderTable(false, paginationLze, setPaginationLze, dataLze)}
                    <Row className="button-row">
                        <ButtonComponent
                            id="seznamPozadavkuZpetBtn"
                            buttontext="ZPET"
                            type="default"
                            onClick={() => {
                                setZmenaPozadavku(undefined);
                            }}
                        />
                        {specialniSluzby && (
                            <ButtonComponent
                                id="seznamPozadavkuSubmitBtn"
                                buttontext="ODESLAT"
                                onClick={() => {
                                    setLoading(true);
                                    ueZaevidovaniPostDVP(specialniSluzby.detail?.id!, {
                                        ...specialniSluzbyDVP?.detailFiltrDVP
                                    })
                                        .then(resp => {
                                            resp.stav?.includes('TIMEOUT')
                                                ? setErrModalMsg('SS_SLUZBA_ERROR_NESKORO')
                                                : renderModal(
                                                      'SS_SEZNAM_MODAL_USPESNE_' + specialniSluzbyDVP?.seznamTyp
                                                  );
                                        })
                                        .catch((err: any) => {
                                            renderModal('RESPONSE_ERROR_' + err.response?.status);
                                        })
                                        .finally(() => {
                                            setLoading(false);
                                        });
                                }}
                            />
                        )}
                    </Row>
                </>
            ) : zmenaPozadavku === 'smazat' ? (
                <>
                    <Title level={5} text="SS_NOVY_SOUBOR_SEZNAM_NADPIS_SMAZANI" />
                    {renderTable(false, paginationZmazani, setPaginationZmazani, dataZmazani)}
                    <Row className="button-row">
                        <ButtonComponent
                            id="seznamPozadavkuZpetBtn"
                            buttontext="ZPET"
                            type="default"
                            onClick={() => {
                                setZmenaPozadavku(undefined);
                            }}
                        />
                        {specialniSluzby && (
                            <ButtonComponent
                                id="seznamPozadavkuSubmitBtn"
                                buttontext={'SMAZAT'}
                                onClick={() => {
                                    setLoading(true);
                                    ueZmazaniDeleteDVP(specialniSluzby.detail?.id!, {
                                        ...specialniSluzbyDVP?.detailFiltrDVP
                                    })
                                        .then(resp => {
                                            resp.stav?.includes('TIMEOUT')
                                                ? renderModal('SS_SLUZBA_ERROR_NESKORO')
                                                : renderModal(
                                                      'SS_SEZNAM_MODAL_USPESNE_' + specialniSluzbyDVP?.seznamTyp
                                                  );
                                        })
                                        .catch((err: any) => {
                                            renderModal('RESPONSE_ERROR_' + err.response?.status);
                                        })
                                        .finally(() => {
                                            setLoading(false);
                                        });
                                }}
                            />
                        )}
                    </Row>
                </>
            ) : zmenaPozadavku === 'edit' || zmenaPozadavku === 'novy' ? (
                <NovyPozadavekDVP
                    setZmenaPozadavku={setZmenaPozadavku}
                    data={data}
                    detailData={selectedDetail && selectedDetail}
                    setSelectedDetail={setSelectedDetail}
                    zmenaPozadavku={zmenaPozadavku}
                />
            ) : (
                <LoadingWrapper loading={loading}>
                    <>
                        {isUEN ? (
                            <Collapse
                                expandIconPosition="left"
                                bordered={false}
                                defaultActiveKey="detail"
                                className="Collapsedivider"
                                expandIcon={({ isActive }) => (
                                    <Row
                                        style={{
                                            width: '100%'
                                        }}
                                        gutter={10}
                                    >
                                        <Col>
                                            <Title level={4} text={`${getLanguageObject(language).UEN_TITLE}`} />
                                        </Col>
                                        <Col>
                                            <DownOutlined
                                                rotate={isActive ? 180 : 0}
                                                style={{
                                                    fontSize: 18,
                                                    top: 10
                                                }}
                                            />
                                        </Col>
                                        <Col flex={10}>
                                            <Divider className="TitleDivider" />
                                        </Col>
                                    </Row>
                                )}
                            >
                                <Panel header={''} key="detail">
                                    <RowTitles
                                        firstText="POCET_CP_MAX"
                                        secondText={formatInputNumberThousand(data?.pocetcpvn)}
                                        paragraph={true}
                                        style={rowTitleStyles}
                                        span={rowsSpan}
                                    />
                                    <RowTitles
                                        firstText="POCET_CP"
                                        secondText={formatInputNumberThousand(data?.pocetcp)}
                                        paragraph={true}
                                        style={rowTitleStyles}
                                        span={rowsSpan}
                                    />
                                </Panel>
                            </Collapse>
                        ) : typprevodu === ETypPrevodu.PROTIABEZZAPL ? (
                            <Collapse
                                expandIconPosition="left"
                                bordered={false}
                                defaultActiveKey="detail"
                                className="Collapsedivider"
                                expandIcon={({ isActive }) => (
                                    <Row
                                        style={{
                                            width: '100%'
                                        }}
                                        gutter={10}
                                    >
                                        <Col>
                                            <Title
                                                level={4}
                                                text={`${
                                                    getLanguageObject(language)
                                                        .DOKUMENT_DODATEK_CP_SPUSOB_PRIKAZ_PROTIZAPLACENI_SHORT
                                                }`}
                                            />
                                        </Col>
                                        <Col>
                                            <DownOutlined
                                                rotate={isActive ? 180 : 0}
                                                style={{
                                                    fontSize: 18,
                                                    top: 10
                                                }}
                                            />
                                        </Col>
                                        <Col flex={10}>
                                            <Divider className="TitleDivider" />
                                        </Col>
                                    </Row>
                                )}
                            >
                                <Panel header={<Title level={4} text={''} />} key="detail">
                                    {renderTable(true, pagination, setPagination, data)}
                                </Panel>
                            </Collapse>
                        ) : (
                            renderTable(true, pagination, setPagination, data)
                        )}
                        {renderSelectedDetail()}
                    </>
                    <ErrModal modalMsg={errModalMsg} setModalMsg={setErrModalMsg} />
                </LoadingWrapper>
            )}
            <ErrModal modalMsg={errModalMsg} setModalMsg={setErrModalMsg} />
            <Modal
                title={<TranslateText>SS_ZAEVIDOVAT_V_CDCP</TranslateText>}
                open={!!zaevidovaniErr.length}
                onCancel={() => {
                    setZaevidovaniErr([]);
                }}
                afterClose={() => {
                    setZaevidovaniErr([]);
                }}
                footer={null}
            >
                <Row>
                    {zaevidovaniErr.includes('castka') && !zaevidovaniErr.includes('bankucet') && (
                        <Text
                            style={{
                                display: 'block'
                            }}
                        >
                            <TranslateText
                                params={[
                                    formatInputNumberThousand(data?.castka || 0),
                                    formatInputNumberThousand(data?.celkcastka || 0),
                                    formatInputNumberThousand(data?.mena || '')
                                ]}
                            >
                                SS_DVP_ZAEVIDOOVAT_ERR1
                            </TranslateText>
                        </Text>
                    )}
                    {zaevidovaniErr.includes('pocetcp') && !zaevidovaniErr.includes('bankucet') && (
                        <Text
                            style={{
                                display: 'block'
                            }}
                        >
                            <TranslateText
                                params={[
                                    formatInputNumberThousand(specialniSluzby?.detail?.pocetcp ?? 0),
                                    formatInputNumberThousand(data?.celkpoccp ?? 0)
                                ]}
                            >
                                SS_DVP_ZAEVIDOOVAT_ERR2
                            </TranslateText>
                        </Text>
                    )}
                    {zaevidovaniErr.includes('bankucet') && (
                        <Text
                            style={{
                                display: 'block'
                            }}
                        >
                            <TranslateText>SS_DVP_ZAEVIDOOVAT_ERR3</TranslateText>
                        </Text>
                    )}
                </Row>
                <Row className="button-row">
                    <ButtonComponent
                        id="seznamPozadavkuZpetAltBtn"
                        buttontext="ZAVRIT"
                        type="default"
                        onClick={() => {
                            setZaevidovaniErr([]);
                        }}
                    />
                </Row>
            </Modal>
            <Modal
                title={
                    <TranslateText>
                        {modalMessage.includes('ERROR_')
                            ? 'CHYBA'
                            : specialniSluzbyDVP?.seznamTyp === ETypSeznam.SMAZANY
                              ? 'SMAZANI_POZADAVKU'
                              : specialniSluzbyDVP?.seznamTyp === ETypSeznam.OVERENY
                                ? 'SS_OVERENI_UCTU'
                                : 'SS_ZAEVIDOVAT_V_CDCP'}
                    </TranslateText>
                }
                open={!!modalMessage}
                onCancel={() => {
                    setModalMessage('');
                    !modalMessage.includes('ERROR_') && setZmenaPozadavku(undefined);
                }}
                afterClose={() => {
                    setModalMessage('');
                    !modalMessage.includes('ERROR_') && setZmenaPozadavku(undefined);
                }}
                footer={
                    <Row className="button-row-modal">
                        <ButtonComponent
                            id="seznamPozadavkuZpetAltBtn"
                            buttontext="ZAVRIT"
                            type="default"
                            onClick={() => {
                                setModalMessage('');
                                !modalMessage.includes('ERROR_') &&
                                    navigateWorkaround(
                                        `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_DETAIL_POZADAVKU}`
                                    );
                            }}
                        />
                    </Row>
                }
            >
                <Row>
                    <div>
                        {!modalMessage.includes('ERROR_') && (
                            <TranslateText>
                                {specialniSluzbyDVP?.seznamTyp === 'SMAZANY'
                                    ? 'SS_SEZNAM_MODAL_USPESNE_POZADAVKY'
                                    : 'SS_SEZNAM_MODAL_USPESNE'}
                            </TranslateText>
                        )}
                        <TranslateText>{modalMessage}</TranslateText>
                    </div>
                    {!modalMessage.includes('ERROR_') && (
                        <div>
                            <Paragraph text={'SS_SEZNAM_MODAL_ZAEVIDOVAT_TEXT'} />
                        </div>
                    )}
                </Row>
            </Modal>
        </>
    );
};

export default DvpSeznam;
